import React from 'react';
import type { AttentionItem, CurrentUserIdentity } from '../../domain/types';
import type { AiReviewPublishEvent } from '@zunelo/shared';
import type { GitHubAiReviewState } from '../../context/slices/useGitHubSlice';
import type { ComposerMentionCandidate } from '../../ui/common/RichTextComposer';
import { BrandIcon, Badge } from '../../ui/common';
import { ProviderItemDetail } from '../detail/ProviderItemDetail';
import { GitHubCardExpanded } from './GitHubCardExpanded';
import { githubPrBadgeLabel } from './githubCardVisuals';

export interface GitHubDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: AttentionItem;
  isCurrentUserApproved: boolean;
  isReRunningChecks: boolean;
  totalChecksConfigured: number;
  currentUser?: CurrentUserIdentity;
  onNavigateToJira: (key: string) => void;
  isJiraRefResolved?: (key: string) => boolean;
  onOpenMergeModal: (e: React.MouseEvent) => void;
  onApprovePR: (e: React.MouseEvent) => void;
  onToggleCommentState: (itemId: string, commentIndex: number) => void;
  onReRunChecks: (e: React.MouseEvent) => void;
  onToggleCheckRun: (itemId: string, checkIndex: number) => void;
  prHtmlUrl?: string | null;
  onOpenPr?: (e: React.MouseEvent) => void;
  aiReview?: GitHubAiReviewState;
  onAiReview?: (e: React.MouseEvent) => void;
  onAiReviewRegenerate?: () => void;
  onAiReviewPublish?: (event: AiReviewPublishEvent) => void;
  onAiReviewDismiss?: () => void;
  onPostComment?: (markdown: string) => Promise<void>;
  onGenerateCommentDraft?: (hint: string) => Promise<string | null>;
  onMentionCandidateUnavailable?: (candidate: ComposerMentionCandidate) => void;
  itemSummaryBusy?: boolean;
  onGenerateItemSummary?: () => void;
}

/**
 * Complete GitHub detail, adopted onto the shared `ProviderItemDetail` shell
 * (tasks.md §4). It renders the SAME `GitHubCardExpanded` content the
 * expanded card already shows (description context, checks, reviewers,
 * comments, AI review, merge flow) inside the larger, dedicated detail
 * surface rather than duplicating that content.
 */
export const GitHubDetailModal: React.FC<GitHubDetailModalProps> = ({
  isOpen,
  onClose,
  item,
  prHtmlUrl,
  onOpenPr,
  ...expandedProps
}) => {
  if (!isOpen) return null;

  return (
    <ProviderItemDetail
      isOpen={isOpen}
      onClose={onClose}
      testId="github-detail-modal"
      ariaLabel={`Detalle de PR ${item.primary || item.title}`}
      header={
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 20px',
            borderBottom: '1px solid var(--z-fill-2)',
            background: 'var(--z-surface-subtle)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <BrandIcon source="github" size={18} />
            <span
              data-testid="github-modal-identity"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--z-text)',
              }}
            >
              {item.primary}
            </span>
            <Badge label={githubPrBadgeLabel(item)} tone={item.merged ? 'ok' : 'accent'} />
            <h1
              data-testid="github-modal-title"
              style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--z-text)' }}
            >
              {item.title}
            </h1>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {prHtmlUrl ? (
              <a
                href={prHtmlUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="github-modal-open-external"
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 32,
                  padding: '0 14px',
                  borderRadius: 8,
                  background: 'var(--z-fill)',
                  color: 'var(--z-text-2)',
                  fontSize: 12.5,
                  fontWeight: 600,
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                <span>Abrir en GitHub</span>
                <span style={{ fontSize: 11 }}>↗</span>
              </a>
            ) : null}
            <button
              type="button"
              data-testid="github-modal-close"
              aria-label="Cerrar modal"
              onClick={onClose}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'var(--z-fill-2)',
                color: 'var(--z-text-2)',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              ✕
            </button>
          </div>
        </header>
      }
    >
      <div style={{ padding: '20px 24px' }}>
        <GitHubCardExpanded item={item} onOpenPr={onOpenPr} {...expandedProps} />
      </div>
    </ProviderItemDetail>
  );
};
